// components/create/CreateBackdrop.js
//
// The create screen's ground: the theme being chosen, live.
//
// ── Why ────────────────────────────────────────────────────────────────────
//
// The app recolours its create screen the moment a theme is picked, and plays
// the chosen effect over the form — "live while creating, as guests will see
// it", as CreateEventScreen puts it. The website painted the blurred cover and
// nothing else, so picking a colour, a video theme or an effect changed
// nothing you could see until after you had published and opened the
// invitation. That is what "the theme is not applying" looks like from
// outside: it was applying, just nowhere visible.
//
// The order here is the invitation's own order (EventInvitation's
// renderBackground), so the preview cannot drift from the result:
//
//   1. video theme   → the looping video, over its colour
//   2. colour theme  → a flat fill
//   3. otherwise     → the cover, blurred
//
// A theme of type 'image' means "use the cover", which is case 3 — the app
// treats it the same way.
//
// ── Why not background-attachment: fixed ───────────────────────────────────
//
// That is what the page used, and it is why the embedded map flickered black
// while scrolling. A fixed background attachment forces the element onto a
// single composited layer that repaints on every scroll frame, and any iframe
// sharing it — the map, the playlist embed — gets repainted with it, showing
// its unpainted backing colour first. A separately positioned fixed child
// gets its own layer and leaves the iframes alone.

import React from 'react';
import EffectLayer from '../EffectLayer';
import supabase from '../../services/supabase';
import '../../styles/CreateBackdrop.scss';

export default function CreateBackdrop({ theme, coverUri, effectId, effectLayer }) {
  const [effect, setEffect] = React.useState(null);

  React.useEffect(() => {
    if (!effectId) { setEffect(null); return undefined; }
    let cancelled = false;
    supabase
      .from('invitation_effects')
      // Every column, for mobile_webp_url (see EffectLayer).
      .select('*')
      .eq('id', effectId)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setEffect(data ?? null); });
    return () => { cancelled = true; };
  }, [effectId]);

  const isVideoTheme = theme?.type === 'video' && theme.videoCdnUrl;
  const isColorTheme = theme?.type === 'color' && theme.value;

  return (
    <>
      <div className="create-backdrop" aria-hidden="true">
        {isVideoTheme ? (
          <>
            {/* The colour shows underneath until the first frame decodes, so
                the screen never flashes black on a slow connection. */}
            <div className="create-backdrop-fill" style={{ background: theme.defaultColor || '#1a1a2e' }} />
            <video
              className="create-backdrop-media"
              src={theme.videoCdnUrl}
              poster={theme.thumbnailCdnUrl || undefined}
              autoPlay
              loop
              muted
              playsInline
            />
            <div className="create-backdrop-scrim create-backdrop-scrim--theme" />
          </>
        ) : isColorTheme ? (
          <div className="create-backdrop-fill" style={{ background: theme.value }} />
        ) : (
          <>
            {coverUri && <img className="create-backdrop-media is-blurred" src={coverUri} alt="" />}
            <div className="create-backdrop-scrim" />
          </>
        )}
      </div>

      {effect && (
        <EffectLayer
          effect={effect}
          title="Effect preview"
          className={`create-backdrop-effect ${effectLayer === 'content' ? 'is-content' : 'is-above'}`}
        />
      )}
    </>
  );
}
